export function singleParam<T extends string>(
  queryParam: string | string[] | undefined | null
): T | undefined
export function singleParam(
  queryParam: string | string[] | undefined | null
): string | undefined {
  if (typeof queryParam === "string") {
    return queryParam
  }

  if (Array.isArray(queryParam)) {
    return queryParam[0]
  }
  return undefined
}

export function positiveNumberParam(
  queryParam: string | string[] | undefined | null
): number | null {
  const value = singleParam(queryParam)
  if (!value) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export function arrayParam<T extends string>(
  queryParam: string | string[] | undefined | null
): T[]
export function arrayParam(
  queryParam: string | string[] | undefined | null
): string[] {
  if (typeof queryParam === "string") {
    return [queryParam]
  }

  if (Array.isArray(queryParam)) {
    return queryParam
  }
  return []
}

// Marketing and tracking query parameters that should be preserved in URLs
// but excluded from canonical URL comparisons. These are the ONLY parameters
// that can be added by marketing/attribution flows.
export const MARKETING_QUERY_PARAMS = [
  // Standard UTM parameters
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_partner",
  // Attribution and referral
  "refId",
  // External platform click IDs
  "fbclid", // Facebook
  "gclid", // Google
  "msclkid", // Microsoft
  "ttclid", // TikTok
  "epik", // Pinterest
] as const

export type MarketingQueryParam = (typeof MARKETING_QUERY_PARAMS)[number]

const MARKETING_PARAMS_SET: Set<string> = new Set(MARKETING_QUERY_PARAMS)

/**
 * Validates that all keys in the provided object are approved marketing parameters.
 * Throws an error if any unapproved parameters are found.
 *
 * @param params - Object with query parameter keys
 * @param context - Context string for error message (e.g., "creator attribution redirect")
 * @throws Error if unapproved parameters are found
 */
export function validateMarketingParams(
  params: Record<string, unknown>,
  context: string
): void {
  const invalidParams = Object.keys(params).filter((key) => {
    return !MARKETING_PARAMS_SET.has(key)
  })

  if (invalidParams.length > 0) {
    throw new Error(
      `Invalid marketing parameters in ${context}: ${invalidParams.join(", ")}. ` +
        `Only these parameters are allowed: ${MARKETING_QUERY_PARAMS.join(", ")}`
    )
  }
}

// Collapse id-like path segments to a stable <ID> placeholder so paths group by
// route shape rather than exploding cardinality on every chat/trip/booking id.
// Mirrors the backend RequestLogging.canonicalize_path regex fallback so that
// request-log path prefixes and Amplitude canonical_path values stay consistent.
const ID_SEGMENT = /^(\d+|[a-z]{1,2}-[A-Za-z0-9]+|message-stream.*)$/

export function canonicalizePath(
  rawPath: string | undefined
): string | undefined {
  if (!rawPath) return rawPath
  const path = rawPath.split("?")[0]
  return path
    .split("/")
    .map((seg) => (ID_SEGMENT.test(seg) ? "<ID>" : seg))
    .join("/")
}
